import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "../../lib/motion";
import { MEMORY_NODES, IMAGES } from "../../lib/siteData";

export const MemoryConstellation = () => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stateRef = useRef({ w: 0, h: 0, mouse: { x: -999, y: -999 }, hovered: -1, particles: [], t: 0 });
  const [active, setActive] = useState(null);
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf;

    const resize = () => {
      const r = wrap.getBoundingClientRect();
      const S = stateRef.current;
      S.w = r.width;
      S.h = r.height;
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      canvas.style.width = r.width + "px";
      canvas.style.height = r.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(22, Math.floor(r.width / 26));
      S.particles = Array.from({ length: n }, () => ({
        x: Math.random() * r.width,
        y: Math.random() * r.height,
        r: Math.random() * 1.3 + 0.3,
        s: Math.random() * 0.22 + 0.04,
        o: Math.random() * 0.5 + 0.15,
      }));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      stateRef.current.mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onLeave = () => { stateRef.current.mouse = { x: -999, y: -999 }; };
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);

    const positioned = () =>
      MEMORY_NODES.map((nd) => ({ ...nd, px: nd.x * stateRef.current.w, py: nd.y * stateRef.current.h }));

    const loop = () => {
      const S = stateRef.current;
      S.t += 0.016;
      ctx.clearRect(0, 0, S.w, S.h);
      const nodes = positioned();

      S.particles.forEach((p) => {
        p.y -= p.s;
        if (p.y < -4) { p.y = S.h + 4; p.x = Math.random() * S.w; }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230,224,210,${p.o * 0.5})`;
        ctx.fill();
      });

      const drawn = new Set();
      nodes.forEach((nd) => {
        (nd.links || []).forEach((lid) => {
          const key = [nd.id, lid].sort().join("-");
          if (drawn.has(key)) return;
          drawn.add(key);
          const t = nodes.find((o) => o.id === lid);
          if (!t) return;
          const hot = S.hovered >= 0 && (nodes[S.hovered].id === nd.id || nodes[S.hovered].id === lid);
          const grad = ctx.createLinearGradient(nd.px, nd.py, t.px, t.py);
          grad.addColorStop(0, hot ? "rgba(197,180,152,0.6)" : "rgba(197,180,152,0.13)");
          grad.addColorStop(1, hot ? "rgba(197,180,152,0.35)" : "rgba(197,180,152,0.05)");
          ctx.strokeStyle = grad;
          ctx.lineWidth = hot ? 1.4 : 0.7;
          ctx.beginPath();
          ctx.moveTo(nd.px, nd.py);
          ctx.lineTo(t.px, t.py);
          ctx.stroke();
        });
      });

      let hov = -1;
      let best = 36 * 36;
      nodes.forEach((nd, i) => {
        const dx = nd.px - S.mouse.x;
        const dy = nd.py - S.mouse.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < best) { best = d2; hov = i; }
      });
      if (hov !== S.hovered) {
        S.hovered = hov;
        setHoverId(hov >= 0 ? nodes[hov].id : null);
      }

      nodes.forEach((nd, i) => {
        const pulse = 1 + Math.sin(S.t * 1.4 + i) * 0.18;
        const isHot = i === S.hovered;
        const baseR = (isHot ? 6 : 3.2) * pulse;
        const gr = ctx.createRadialGradient(nd.px, nd.py, 0, nd.px, nd.py, isHot ? 48 : 26);
        gr.addColorStop(0, isHot ? "rgba(197,180,152,0.5)" : "rgba(197,180,152,0.26)");
        gr.addColorStop(1, "rgba(197,180,152,0)");
        ctx.fillStyle = gr;
        ctx.beginPath();
        ctx.arc(nd.px, nd.py, isHot ? 48 : 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(nd.px, nd.py, baseR, 0, Math.PI * 2);
        ctx.fillStyle = isHot ? "#F7F2E7" : "#E9E2D2";
        ctx.fill();
        if (isHot) {
          ctx.font = "500 13px Manrope, sans-serif";
          ctx.fillStyle = "rgba(249,248,246,0.92)";
          ctx.fillText(nd.label, nd.px + 14, nd.py - 12);
        }
      });

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  const shown = active || (hoverId && MEMORY_NODES.find((n) => n.id === hoverId)) || null;

  return (
    <section
      id="experience"
      data-testid="constellation-section"
      className="relative py-28 lg:py-40 bg-[#0B0C0F] text-[#F9F8F6] overflow-hidden"
    >
      <div className="absolute inset-0 -z-0 opacity-40" aria-hidden>
        <img src={IMAGES.stars} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0F] via-[#0B0C0F]/60 to-[#0B0C0F]" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#C5B498] mb-6">
            Memory Constellation
          </p>
          <h2 className="font-serif font-light tracking-tight leading-[1.02] text-[clamp(2rem,5.5vw,4.75rem)] max-w-3xl text-balance">
            A life, mapped in light.
          </h2>
          <p className="mt-6 max-w-xl text-[#9A9C9F] leading-relaxed">
            Approved memories connect through luminous threads, including voice, imagery,
            handwritten notes, places, values, and the people who shaped a life. Each point
            retains its source, permissions, and place within the person's governed legacy.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-stretch">
          <Reveal className="lg:col-span-7">
            <div
              ref={wrapRef}
              className="relative w-full h-[52vh] lg:h-[62vh] rounded-sm border border-[#2A2C31] overflow-hidden"
            >
              <canvas
                ref={canvasRef}
                data-testid="constellation-canvas"
                onClick={() => {
                  const h = stateRef.current.hovered;
                  if (h >= 0) setActive(MEMORY_NODES[h]);
                }}
                className="absolute inset-0 cursor-pointer"
              />
            </div>
          </Reveal>

          <div className="lg:col-span-5 flex flex-col justify-between">
            <div className="min-h-[180px]">
              <AnimatePresence mode="wait">
                {shown ? (
                  <motion.div
                    key={shown.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12, transition: { duration: 0.22 } }}
                    transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
                    data-testid="constellation-fragment"
                  >
                    <span className="text-[11px] uppercase tracking-[0.35em] text-[#C5B498]">
                      {shown.type}
                    </span>
                    <h3 className="font-serif font-light text-[clamp(1.75rem,3vw,2.6rem)] tracking-tight mt-2 mb-4">
                      {shown.label}
                    </h3>
                    <p className="font-serif italic text-[#D8D4CA] text-lg sm:text-xl leading-relaxed">
                      {shown.note}
                    </p>
                  </motion.div>
                ) : (
                  <motion.p
                    key="prompt"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="font-serif italic text-[#8b8d92] text-xl"
                  >
                    Hover a star to remember. Click to hold it a while.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <div className="mt-8">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#8b8d92] mb-4">
                The threads
              </p>
              <div className="flex flex-wrap gap-2.5">
                {MEMORY_NODES.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`constellation-node-${n.id}`}
                    onMouseEnter={() => setHoverId(n.id)}
                    onClick={() => setActive(n)}
                    className={`px-4 py-2 rounded-full text-[13px] tracking-wide border transition-colors duration-300 ${
                      (shown && shown.id === n.id)
                        ? "border-[#C5B498] text-[#F9F8F6] bg-white/[0.04]"
                        : "border-[#2A2C31] text-[#9A9C9F] hover:border-[#C5B498]/60 hover:text-[#F9F8F6]"
                    }`}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
